import { get, set } from 'idb-keyval';
import Phaser from 'phaser';
import { SAVE_VERSION, STORAGE_KEY } from '../config';
import { STARTER_ROSTER } from './data/athletes';
import { CloudSync, type GoogleUser } from './CloudSync';
import { CrazyGames } from './crazygames';
import { nextTrainCost, recoverFatigue } from './Economy';
import { getLang, setLang, type Lang } from './i18n';
import type { Athlete, LeaderboardRecord, SaveState, StatKey } from './types';

/**
 * Single source of truth for meta-game state. Persists to IndexedDB (with a
 * localStorage fallback) and emits change events the UI subscribes to.
 *
 * Usage: `await SaveManager.instance.load()` once at boot, then read/mutate via methods.
 */
export class SaveManager extends Phaser.Events.EventEmitter {
  private static _instance: SaveManager | undefined;
  private state: SaveState;
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private cloudTimer: ReturnType<typeof setTimeout> | null = null;

  static get instance(): SaveManager {
    if (!SaveManager._instance) SaveManager._instance = new SaveManager();
    return SaveManager._instance;
  }

  private constructor() {
    super();
    this.state = SaveManager.freshState();
  }

  private static freshState(): SaveState {
    return {
      coins: 100,
      credits: 3,
      profile: { teamName: '' },
      roster: structuredClone(STARTER_ROSTER),
      unlockedDisciplines: ['sprint_100m', 'long_jump', 'hurdles', 'javelin', 'high_jump', 'triple_jump', 'shot_put', 'hammer_throw', 'pole_vault', 'discus', 'weightlifting', 'swim_50m', 'diving', 'archery'],
      leaderboard: [],
      career: { tournamentsWon: 0, medals: 0, gold: 0, silver: 0, bronze: 0 },
      version: SAVE_VERSION,
    };
  }

  /** Load persisted state, migrating or resetting on version mismatch. */
  async load(): Promise<void> {
    let raw: SaveState | undefined = this.readDataModule();
    if (!raw) {
      try {
        raw = await get<SaveState>(STORAGE_KEY);
      } catch {
        raw = SaveManager.readLocalFallback();
      }
    }
    if (raw && raw.version === SAVE_VERSION) {
      this.state = raw;
    } else if (raw) {
      this.state = this.migrate(raw);
    }
    this.reconcileRoster();
    this.reconcileOwnership();
    this.recoverFatigueWhileAway();
    // Default to ENGLISH on first run (the game targets an international audience, e.g. CrazyGames;
    // even the very first name-entry screen reads in English). Players switch language on the menu.
    if (!this.state.lang) { this.state.lang = 'en'; this.schedulePersist(); }
    // Auto-name on first run so the game opens straight to the menu (gameplay one tap away) instead
    // of a blocking name-entry screen — players rename later from the badge. Better first impression.
    if (!this.state.profile.teamName?.trim()) { this.state.profile.teamName = SaveManager.randomTeamName(); this.schedulePersist(); }
    setLang(this.state.lang as Lang);
    this.emit('loaded', this.state);
  }

  get lang(): Lang {
    return getLang();
  }

  /** Switch the UI language and persist it. */
  setLanguage(l: Lang): void {
    this.state.lang = l;
    setLang(l);
    this.emit('langChanged', l);
    this.schedulePersist();
  }

  // ── Cloud save (optional Google Sign-In) ────────────────────────────────────
  /** Set up sign-in. Call once after load(). A signed-in session syncs progress across devices. */
  async initCloud(): Promise<void> {
    await CloudSync.init((user) => {
      if (user) void this.onCloudSignIn();
      else this.emit('cloudChanged', null);
    });
    if (CloudSync.isSignedIn) void this.onCloudSignIn();
    this.emit('cloudChanged', CloudSync.user);
  }

  /** Adopt the server's authoritative coin balance (after an Arena transaction). */
  setServerCoins(n: number | null | undefined): void {
    if (n == null || !Number.isFinite(n) || n === this.state.coins) return;
    this.state.coins = Math.max(0, Math.round(n));
    this.emit('coinsChanged', this.state.coins);
    this.schedulePersist();
  }

  /** Cloud save is always available (local account); Google is an extra option. */
  get cloudEnabled(): boolean { return true; }
  get googleEnabled(): boolean { return CloudSync.googleEnabled; }
  get signedIn(): boolean { return CloudSync.isSignedIn; }
  get cloudUser(): GoogleUser | null { return CloudSync.user; }
  promptSignIn(): void { CloudSync.promptOneTap(); }
  signOut(): void { CloudSync.signOut(); this.emit('cloudChanged', null); }

  /** On sign-in: pull the cloud blob, merge with local progress, then push the merged result. */
  private async onCloudSignIn(): Promise<void> {
    const remote = await CloudSync.loadCloud();
    if (remote?.blob) {
      try {
        const cloud = JSON.parse(remote.blob) as SaveState;
        this.state = this.mergeStates(this.state, cloud, remote.updatedAt);
        this.reconcileRoster();
        setLang((this.state.lang as Lang) ?? getLang());
      } catch { /* malformed cloud blob — keep local */ }
    }
    this.state.lastSeenAt = Date.now();
    await this.persist();
    void this.pushCloud();
    this.emit('loaded', this.state);
    this.emit('cloudChanged', CloudSync.user);
  }

  private hasProgress(s: SaveState): boolean {
    return (s.coins ?? 100) !== 100
      || (s.leaderboard?.length ?? 0) > 0
      || (s.unlockedAthletes?.length ?? 0) > 0
      || (s.career?.medals ?? 0) > 0
      || !!s.profile?.teamName?.trim();
  }

  /**
   * Merge a local and a cloud SaveState. The side that actually has progress becomes the base
   * (if both do, the more recently updated one wins); then unlocks/medals/cosmetics from the
   * other side are unioned in as a safety net, so a rare two-device conflict never DROPS a
   * purchased athlete or earned medal. Adopting the cloud base also adopts its clientId, which
   * unifies the player's leaderboard identity across devices.
   */
  private mergeStates(local: SaveState, cloud: SaveState, cloudUpdatedAt: number): SaveState {
    const localTs = local.lastSeenAt ?? 0;
    const localHas = this.hasProgress(local), cloudHas = this.hasProgress(cloud);
    let primary: SaveState, secondary: SaveState;
    if (cloudHas && !localHas) { primary = cloud; secondary = local; }
    else if (localHas && !cloudHas) { primary = local; secondary = cloud; }
    else if (cloudUpdatedAt >= localTs) { primary = cloud; secondary = local; }
    else { primary = local; secondary = cloud; }

    const merged = structuredClone(primary);
    const uniq = (a?: string[], b?: string[]): string[] => Array.from(new Set([...(a ?? []), ...(b ?? [])]));
    merged.unlockedAthletes = uniq(primary.unlockedAthletes, secondary.unlockedAthletes);
    merged.unlockedDisciplines = uniq(primary.unlockedDisciplines, secondary.unlockedDisciplines);
    merged.cosmetics = {
      owned: uniq(primary.cosmetics?.owned, secondary.cosmetics?.owned),
      equipped: { ...(secondary.cosmetics?.equipped ?? {}), ...(primary.cosmetics?.equipped ?? {}) },
    };
    const pc = primary.career, sc = secondary.career;
    merged.career = {
      tournamentsWon: Math.max(pc?.tournamentsWon ?? 0, sc?.tournamentsWon ?? 0),
      medals: Math.max(pc?.medals ?? 0, sc?.medals ?? 0),
      gold: Math.max(pc?.gold ?? 0, sc?.gold ?? 0),
      silver: Math.max(pc?.silver ?? 0, sc?.silver ?? 0),
      bronze: Math.max(pc?.bronze ?? 0, sc?.bronze ?? 0),
    };
    merged.version = SAVE_VERSION;
    return merged;
  }

  /** Debounced push of the full state to the cloud (only while signed in). */
  private schedulePush(): void {
    if (!CloudSync.isSignedIn) return;
    if (this.cloudTimer) clearTimeout(this.cloudTimer);
    this.cloudTimer = setTimeout(() => void this.pushCloud(), 2500);
  }

  private async pushCloud(): Promise<void> {
    if (!CloudSync.isSignedIn) return;
    try { await CloudSync.pushCloud(JSON.stringify(this.state)); } catch { /* offline — retried next change */ }
  }

  /**
   * Only the 5 free athletes start unlocked; the rest are bought with coins. A one-off pre-launch
   * reset re-locks any paid athletes that earlier builds had auto-granted, so the coin economy
   * applies to everyone (players keep their coins to re-unlock what they want).
   */
  private reconcileOwnership(): void {
    this.state.unlockedAthletes ??= [];
    this.state.fixes ??= {};
    if (!this.state.fixes.economyRelock) {
      this.state.unlockedAthletes = [];
      this.state.fixes.economyRelock = true;
      this.schedulePersist();
    }
  }

  /** Recover every athlete's fatigue for the real time elapsed since the last load. */
  private recoverFatigueWhileAway(): void {
    const now = Date.now();
    const elapsed = this.state.lastSeenAt ? now - this.state.lastSeenAt : 0;
    if (elapsed > 0) {
      for (const a of this.state.roster) a.fatigue = recoverFatigue(a.fatigue, elapsed);
    }
    this.state.lastSeenAt = now;
    this.schedulePersist();
  }

  /**
   * The roster is persisted, so athletes added to STARTER_ROSTER in a later build are
   * missing from existing saves. Add any new starter athletes (by id) without touching
   * the player's coins/progress, then persist.
   */
  private reconcileRoster(): void {
    let changed = false;
    for (const starter of STARTER_ROSTER) {
      const existing = this.state.roster.find((a) => a.id === starter.id);
      if (!existing) {
        this.state.roster.push(structuredClone(starter));
        changed = true;
      } else {
        // Refresh identity/cosmetic fields from the latest build (names, flags, skin,
        // rebalanced stats) while preserving the player's progress (level, fatigue).
        existing.name = starter.name;
        existing.nickname = starter.nickname;
        existing.nationality = starter.nationality;
        existing.species = starter.species;
        existing.skin = starter.skin;
        existing.specialty = starter.specialty;
        existing.stats = { ...starter.stats };
        existing.unlockPrice = starter.unlockPrice;
        changed = true;
      }
    }
    // Same problem for disciplines: existing saves miss ones added in later builds.
    for (const id of SaveManager.freshState().unlockedDisciplines) {
      if (!this.state.unlockedDisciplines.includes(id)) {
        this.state.unlockedDisciplines.push(id);
        changed = true;
      }
    }
    // Backfill medal-tier counters added in a later build (older saves only had `medals`).
    const c = this.state.career as Partial<SaveState['career']>;
    if (c.gold === undefined || c.silver === undefined || c.bronze === undefined) {
      c.gold = c.gold ?? 0; c.silver = c.silver ?? 0; c.bronze = c.bronze ?? 0;
      changed = true;
    }
    // Assign a stable device id once (leaderboard identity that survives a rename).
    if (!this.state.clientId) {
      this.state.clientId = SaveManager.genId();
      changed = true;
    }
    // One-off fixes. Diving scoring was rebalanced after launch (old dives maxed at 100);
    // drop the stale diving records once so fair records can be set under the new formula.
    this.state.fixes ??= {};
    // Fatigue used to accumulate (+18/race) but was never read or recovered, so old saves can
    // sit near 100. Now that it gently lowers performance, zero it once so nobody is silently
    // nerfed the moment the mechanic goes live — it starts fresh as a real system from here.
    if (!this.state.fixes.fatigueActivation) {
      for (const a of this.state.roster) a.fatigue = 0;
      this.state.fixes.fatigueActivation = true;
      changed = true;
    }
    // Cosmetics shop is hidden (not ready): wipe anything bought/equipped while testing, once.
    // The flag stops this from re-running once the shop is live again.
    if (!this.state.fixes.cosmeticsReset) {
      this.state.cosmetics = { owned: [], equipped: {} };
      this.state.fixes.cosmeticsReset = true;
      changed = true;
    }
    if (!this.state.fixes.divingRebalance) {
      const before = this.state.leaderboard.length;
      this.state.leaderboard = this.state.leaderboard.filter((r) => r.disciplineId !== 'diving');
      this.state.fixes.divingRebalance = true;
      if (this.state.leaderboard.length !== before) changed = true;
      changed = true;
    }
    if (changed) this.schedulePersist();
  }

  private static genId(): string {
    try {
      const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
      if (c?.randomUUID) return c.randomUUID();
    } catch { /* fall through */ }
    return `z-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
  }

  /** Future-proofing hook: bump SAVE_VERSION and translate older shapes here. */
  private migrate(old: SaveState): SaveState {
    return { ...SaveManager.freshState(), ...old, version: SAVE_VERSION };
  }

  private static readLocalFallback(): SaveState | undefined {
    try {
      const s = localStorage.getItem(STORAGE_KEY);
      return s ? (JSON.parse(s) as SaveState) : undefined;
    } catch {
      return undefined;
    }
  }

  get(): Readonly<SaveState> {
    return this.state;
  }

  get coins(): number {
    return this.state.coins;
  }

  get roster(): readonly Athlete[] {
    return this.state.roster;
  }

  /** True if the athlete is free or already bought. */
  isUnlocked(athlete: Athlete): boolean {
    return !athlete.unlockPrice || (this.state.unlockedAthletes?.includes(athlete.id) ?? false);
  }

  /** Athletes the player can actually race/train (free + bought). */
  ownedRoster(): readonly Athlete[] {
    return this.state.roster.filter((a) => this.isUnlocked(a));
  }

  private cosmeticsState(): { owned: string[]; equipped: Record<string, Record<string, string>> } {
    this.state.cosmetics ??= { owned: [], equipped: {} };
    return this.state.cosmetics;
  }

  ownsCosmetic(id: string): boolean {
    return this.cosmeticsState().owned.includes(id);
  }

  /** Buy a cosmetic with coins (one-time). Returns false if already owned or unaffordable. */
  buyCosmetic(id: string, price: number): boolean {
    const cos = this.cosmeticsState();
    if (cos.owned.includes(id) || !this.spendCoins(price)) return false;
    cos.owned.push(id);
    this.emit('cosmeticsChanged');
    this.schedulePersist();
    return true;
  }

  /** Equip (or unequip with id=null) a cosmetic in a slot for one athlete. */
  equipCosmetic(athleteId: string, slot: string, id: string | null): void {
    const cos = this.cosmeticsState();
    const eq = (cos.equipped[athleteId] ??= {});
    if (id) eq[slot] = id; else delete eq[slot];
    this.emit('cosmeticsChanged');
    this.schedulePersist();
  }

  /** The cosmetics this athlete currently has equipped, by slot. */
  equippedFor(athleteId: string): Record<string, string> {
    return this.cosmeticsState().equipped[athleteId] ?? {};
  }

  /** Buy a locked athlete; returns false if already owned, free, or unaffordable. */
  unlockAthlete(athleteId: string): boolean {
    const athlete = this.state.roster.find((a) => a.id === athleteId);
    if (!athlete || !athlete.unlockPrice || this.isUnlocked(athlete)) return false;
    if (!this.spendCoins(athlete.unlockPrice)) return false;
    this.state.unlockedAthletes ??= [];
    this.state.unlockedAthletes.push(athleteId);
    this.emit('rosterChanged', this.state.roster);
    this.schedulePersist();
    return true;
  }

  addCoins(delta: number): void {
    this.state.coins = Math.max(0, this.state.coins + delta);
    this.emit('coinsChanged', this.state.coins);
    this.schedulePersist();
  }

  /** Rewarded-ad coin bonus, gated by a short cooldown so it can't be spammed. */
  static readonly REWARD_COOLDOWN_MS = 90_000;
  rewardReadyInMs(): number {
    return Math.max(0, SaveManager.REWARD_COOLDOWN_MS - (Date.now() - (this.state.lastRewardAt ?? 0)));
  }
  canClaimReward(): boolean {
    return this.rewardReadyInMs() <= 0;
  }
  grantRewardCoins(coins: number): void {
    this.state.lastRewardAt = Date.now();
    this.addCoins(coins); // emits coinsChanged + persists (and pushes to cloud if signed in)
  }

  /** Spend coins atomically; returns false (and changes nothing) if the player can't afford it. */
  spendCoins(amount: number): boolean {
    if (amount <= 0 || this.state.coins < amount) return false;
    this.state.coins -= amount;
    this.emit('coinsChanged', this.state.coins);
    this.schedulePersist();
    return true;
  }

  /**
   * Buy one training level for a stat (the repeatable coin sink). Returns false if the stat is
   * maxed or the player can't afford the next level. The bonus is permanent and stacks on the
   * base stat via Economy.effectiveStats.
   */
  trainStat(athleteId: string, stat: StatKey): boolean {
    const athlete = this.state.roster.find((a) => a.id === athleteId);
    if (!athlete) return false;
    const cost = nextTrainCost(athlete, stat);
    if (cost == null || !this.spendCoins(cost)) return false;
    athlete.train ??= {};
    athlete.train[stat] = (athlete.train[stat] ?? 0) + 1;
    this.emit('rosterChanged', this.state.roster);
    this.schedulePersist();
    return true;
  }

  addMedal(tier: 'gold' | 'silver' | 'bronze'): void {
    this.state.career.medals += 1;
    this.state.career[tier] += 1;
    this.emit('careerChanged', this.state.career);
    this.schedulePersist();
  }

  get career(): Readonly<SaveState['career']> {
    return this.state.career;
  }

  get teamName(): string {
    return this.state.profile.teamName;
  }

  get clientId(): string {
    return this.state.clientId ?? '';
  }

  hasProfile(): boolean {
    return this.state.profile.teamName.trim().length > 0;
  }

  setTeamName(name: string): void {
    const clean = SaveManager.sanitizeName(name);
    this.state.profile.teamName = clean || this.state.profile.teamName || SaveManager.randomTeamName();
    this.emit('profileChanged', this.state.profile);
    this.schedulePersist();
  }

  // Block real public figures / slurs from public names (the "Donald Trump" test name flagged a
  // content/copyright concern on portals). Returns '' if the name should be rejected.
  private static readonly NAME_BLOCK = ['trump', 'biden', 'obama', 'putin', 'zelensky', 'hitler', 'stalin', 'mussolini', 'berlusconi', 'meloni', 'salvini', 'macron', 'musk', 'nazi', 'isis', 'hamas'];
  static sanitizeName(name: string): string {
    const n = String(name ?? '').replace(/\s+/g, ' ').trim().slice(0, 24);
    const low = n.toLowerCase();
    if (SaveManager.NAME_BLOCK.some((b) => low.includes(b))) return '';
    return n;
  }
  static randomTeamName(): string {
    const a = ['Turbo', 'Wild', 'Mega', 'Zoom', 'Blaze', 'Storm', 'Roar', 'Nitro', 'Cosmo', 'Rex'];
    const b = ['Squad', 'Team', 'Stars', 'Crew', 'Pride', 'Force'];
    const pick = (arr: string[]): string => arr[Math.floor(Math.random() * arr.length)]!;
    return `${pick(a)} ${pick(b)}`;
  }

  addRecord(rec: LeaderboardRecord): void {
    this.state.leaderboard.push(rec);
    // Keep the table from growing unbounded; we only ever show the best few anyway.
    if (this.state.leaderboard.length > 200) {
      this.state.leaderboard = this.state.leaderboard.slice(-200);
    }
    this.emit('leaderboardChanged', this.state.leaderboard);
    this.schedulePersist();
  }

  /** Medal tally reconstructed from every stored result by finishing rank (1/2/3 = podium). */
  medalTally(): { gold: number; silver: number; bronze: number } {
    let gold = 0, silver = 0, bronze = 0;
    for (const r of this.state.leaderboard) {
      if (r.disciplineId === 'coins' || r.disciplineId === 'medals') continue;
      if (r.rank === 1) gold++; else if (r.rank === 2) silver++; else if (r.rank === 3) bronze++;
    }
    return { gold, silver, bronze };
  }

  /** Best records for a discipline, sorted by metric (time asc / distance desc). */
  topRecords(disciplineId: string, kind: 'time' | 'distance', limit = 10): LeaderboardRecord[] {
    return this.state.leaderboard
      .filter((r) => r.disciplineId === disciplineId)
      .sort((a, b) => (kind === 'time' ? a.metric - b.metric : b.metric - a.metric))
      .slice(0, limit);
  }

  updateAthlete(id: string, mutate: (a: Athlete) => void): void {
    const athlete = this.state.roster.find((a) => a.id === id);
    if (!athlete) return;
    mutate(athlete);
    this.emit('rosterChanged', this.state.roster);
    this.schedulePersist();
  }

  /** Debounced write so rapid mutations don't thrash IndexedDB. */
  private schedulePersist(): void {
    if (this.persistTimer) clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => void this.persist(), 250);
  }

  /** Read the save from the CrazyGames Data Module (the persistent store on their platform). */
  private readDataModule(): SaveState | undefined {
    if (!CrazyGames.hasData) return undefined;
    const s = CrazyGames.dataGet(STORAGE_KEY);
    if (!s) return undefined;
    try { return JSON.parse(s) as SaveState; } catch { return undefined; }
  }

  private async persist(): Promise<void> {
    const json = JSON.stringify(this.state);
    // On CrazyGames, the Data Module is the persistent store (survives their CDN, syncs to the user).
    if (CrazyGames.hasData) CrazyGames.dataSet(STORAGE_KEY, json);
    // Always keep a local copy too (offline + off-portal + redundancy).
    try {
      await set(STORAGE_KEY, this.state);
    } catch {
      try {
        localStorage.setItem(STORAGE_KEY, json);
      } catch {
        // Storage unavailable (private mode / quota) — keep playing in-memory.
      }
    }
    // Mirror to our own cloud (debounced) when the player is signed in with an account.
    this.schedulePush();
  }
}
